// Phase JA.4 — SlipBuilder UI.
//
// A floating in-page panel showing the user's in-progress accumulator
// (legs added from the Signal Menu rows). Composes existing super-acca
// primitives only — no new visual primitives.
//
// N-rule compliance (locked):
//   - NO stake recommendation. The stake field is empty by default, the
//     user types their own number, and STRIKZA never suggests a value.
//   - NO "you should bet £X" copy.
//   - The panel renders combined odds and leg count regardless; it
//     renders potential-return only when the user typed a stake.
//
// Doctrine carry-forward:
//   - Anon-readable: anon users can build a draft slip locally.
//   - Locking requires auth — anon Lock CTA opens /signin.
//   - No fabrication: nothing is invented; locked slip mirrors the DB
//     row.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Badge, Eyebrow, LegRow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { trackEvent } from "../../conversion/trackEvent.ts";
import { useUserSlips, type LockSlipInput, type UserSlip } from "../../free-tier/useUserSlips.ts";
import { BankToGoalCta } from "../sika-extras/BankToGoalCta.tsx";
import {
  combinedOdds,
  loadDraft,
  parseStake,
  potentialReturn,
  removeLeg,
  saveDraft,
  setStakeInput as setStakeInputHelper,
  clearDraft,
  EMPTY_DRAFT,
  type DraftSlip,
} from "./slipBuilder.ts";
import type { UserSlipLeg } from "../../free-tier/useUserSlips.ts";

export interface SlipBuilderProps {
  /** When set, called when user clicks Lock and is not authenticated. */
  onAuthRequired?: () => void;
  /** Optional latest locked slip (from useUserSlips). Renders read-only summary above the draft. */
  latestLockedSlip?: UserSlip | null;
}

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";
const fontStat = "var(--sa-font-stat)";

const panelStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const browserStorage = {
  read(k: string): string | null {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(k) : null;
    } catch {
      return null;
    }
  },
  write(k: string, v: string): void {
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(k, v);
    } catch {
      /* ignore */
    }
  },
  remove(k: string): void {
    try {
      if (typeof window !== "undefined") window.localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

/** External hook on the SlipBuilder API used by parent (page) for "+ add". */
export interface SlipBuilderApi {
  draft: DraftSlip;
  addLeg(leg: UserSlipLeg): void;
  removeLegById(signalId: string): void;
}

/** Hook variant used by DailyAccaPage to push legs into the builder. */
export function useSlipDraft(): {
  draft: DraftSlip;
  setDraft: React.Dispatch<React.SetStateAction<DraftSlip>>;
} {
  const [draft, setDraft] = useState<DraftSlip>(() =>
    loadDraft(browserStorage.read),
  );
  useEffect(() => {
    saveDraft(draft, browserStorage.write);
  }, [draft]);
  return { draft, setDraft };
}

interface InternalProps extends SlipBuilderProps {
  draft: DraftSlip;
  setDraft: React.Dispatch<React.SetStateAction<DraftSlip>>;
}

export function SlipBuilder(props: InternalProps): JSX.Element {
  const { draft, setDraft, onAuthRequired, latestLockedSlip } = props;
  const { user } = useAuthSession();
  const { lockSlip, locking } = useUserSlips();
  const [lockError, setLockError] = useState<string | null>(null);

  const odds = useMemo(() => combinedOdds(draft.legs), [draft.legs]);
  const stakeNumber = useMemo(() => parseStake(draft.stakeInput), [draft.stakeInput]);
  const ret = useMemo(
    () => potentialReturn(draft.legs, stakeNumber),
    [draft.legs, stakeNumber],
  );

  const onRemove = (signalId: string): void => {
    trackEvent("slip_leg_removed", { signal_id: signalId });
    setDraft((d) => removeLeg(d, signalId));
  };

  const onClear = (): void => {
    setDraft(() => EMPTY_DRAFT);
    clearDraft(browserStorage.remove);
  };

  const onLock = async (): Promise<void> => {
    setLockError(null);
    if (!user) {
      onAuthRequired?.();
      return;
    }
    if (draft.legs.length === 0) {
      setLockError("Add at least one leg.");
      return;
    }
    const input: LockSlipInput = {
      legs: draft.legs,
      combinedOdds: odds,
      stakeAmount: stakeNumber,
    };
    const res = await lockSlip(input);
    if (res.ok) {
      trackEvent("slip_locked", {
        leg_count: draft.legs.length,
        combined_odds: odds,
        has_stake: stakeNumber !== null,
      });
      onClear();
    } else if (res.error === "auth-required") {
      onAuthRequired?.();
    } else {
      setLockError(res.error);
    }
  };

  return (
    <div style={panelStyle} data-testid="slip-builder">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Eyebrow>Slip Builder · Today</Eyebrow>
        <span style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-fg-muted)" }}>
          {draft.legs.length} LEG{draft.legs.length === 1 ? "" : "S"}
        </span>
      </div>

      {latestLockedSlip ? (
        <LockedSlipSummary slip={latestLockedSlip} />
      ) : null}

      {draft.legs.length === 0 ? (
        <div
          data-testid="slip-builder-empty"
          style={{
            fontFamily: fontBody,
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          Tap{" "}
          <span style={{ fontFamily: fontMono, color: "var(--sa-accent-saffron)" }}>
            + ADD TO SLIP
          </span>{" "}
          on any signal below to start building your accumulator. STRIKZA never recommends a stake — type your own.
        </div>
      ) : (
        <div>
          {draft.legs.map((leg, i) => (
            <div
              key={leg.signal_id}
              style={{ position: "relative" }}
              data-testid="slip-builder-leg"
            >
              <LegRow
                index={i + 1}
                market={leg.market}
                selection={leg.selection}
                odds={leg.odds}
                category={tierCategory(leg.tier)}
              />
              <button
                type="button"
                onClick={() => onRemove(leg.signal_id)}
                aria-label={`Remove leg ${i + 1}`}
                style={{
                  position: "absolute",
                  top: 14,
                  right: 0,
                  background: "transparent",
                  border: "1px solid var(--sa-hairline)",
                  color: "var(--sa-fg-muted)",
                  fontFamily: fontMono,
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  borderRadius: 3,
                  padding: "2px 6px",
                  cursor: "pointer",
                }}
              >
                REMOVE
              </button>
            </div>
          ))}
        </div>
      )}

      {draft.legs.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            gap: 18,
            alignItems: "center",
            paddingTop: 14,
            borderTop: "1px solid var(--sa-hairline)",
          }}
        >
          <Stat label="Combined" value={`${odds.toFixed(2)}×`} accent />
          <div>
            <div
              style={{
                fontFamily: fontStat,
                fontSize: 11,
                letterSpacing: "0.18em",
                color: "var(--sa-fg-muted)",
                textTransform: "uppercase",
              }}
            >
              Stake (your choice)
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={draft.stakeInput}
              placeholder=""
              aria-label="Your stake (optional)"
              data-testid="slip-builder-stake-input"
              onChange={(e) =>
                setDraft((d) => setStakeInputHelper(d, e.target.value))
              }
              style={{
                marginTop: 6,
                width: "100%",
                maxWidth: 180,
                background: "var(--sa-bg-base)",
                color: "var(--sa-fg-primary)",
                border: "1px solid var(--sa-hairline)",
                borderRadius: 4,
                padding: "8px 10px",
                fontFamily: fontMono,
                fontSize: 13,
                outline: "none",
              }}
            />
            <div
              style={{
                marginTop: 4,
                fontFamily: fontMono,
                fontSize: 9.5,
                letterSpacing: "0.14em",
                color: "var(--sa-fg-muted)",
              }}
            >
              STRIKZA NEVER RECOMMENDS A STAKE
            </div>
          </div>
          {ret !== null ? (
            <Stat label="Potential return" value={`${ret.toFixed(2)}`} />
          ) : (
            <Stat label="Potential return" value="—" />
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14 }}>
        <button
          type="button"
          onClick={onClear}
          disabled={draft.legs.length === 0}
          style={{
            background: "transparent",
            border: "1px solid var(--sa-hairline)",
            color: "var(--sa-fg-muted)",
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: "0.18em",
            padding: "8px 12px",
            borderRadius: 4,
            cursor: draft.legs.length === 0 ? "not-allowed" : "pointer",
            opacity: draft.legs.length === 0 ? 0.5 : 1,
          }}
        >
          CLEAR DRAFT
        </button>
        <button
          type="button"
          onClick={() => void onLock()}
          disabled={locking || draft.legs.length === 0}
          data-testid="slip-builder-lock"
          style={{
            background: draft.legs.length === 0 ? "transparent" : "var(--sa-accent-saffron)",
            border: "1px solid var(--sa-accent-saffron)",
            color: draft.legs.length === 0 ? "var(--sa-fg-muted)" : "var(--sa-bg-base)",
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: "0.22em",
            padding: "10px 18px",
            borderRadius: 4,
            cursor: draft.legs.length === 0 || locking ? "not-allowed" : "pointer",
            fontWeight: 600,
          }}
        >
          {locking ? "LOCKING…" : user ? "LOCK SLIP" : "SIGN IN TO LOCK"}
        </button>
      </div>
      {lockError ? (
        <div
          role="alert"
          style={{
            fontFamily: fontBody,
            fontSize: 12.5,
            color: "var(--sa-signal-red)",
          }}
        >
          {lockError}
        </div>
      ) : null}
    </div>
  );
}

function tierCategory(tier: "tier_a" | "tier_b" | "tier_c"): string {
  if (tier === "tier_a") return "TIER-A";
  if (tier === "tier_b") return "TIER-B";
  return "TIER-C";
}

function LockedSlipSummary({ slip }: { slip: UserSlip }): JSX.Element {
  const variant: "amber" | "success" | "danger" | "info" =
    slip.outcome === "won"
      ? "success"
      : slip.outcome === "lost"
        ? "danger"
        : slip.status === "locked"
          ? "info"
          : "amber";
  const label =
    slip.outcome === "won"
      ? "WON"
      : slip.outcome === "lost"
        ? "LOST"
        : slip.outcome === "void"
          ? "VOID"
          : slip.outcome === "partial"
            ? "PARTIAL"
            : "LOCKED";
  return (
    <div
      data-testid="slip-builder-locked-summary"
      style={{
        border: "1px solid var(--sa-hairline)",
        borderRadius: 6,
        padding: "12px 14px",
        display: "flex",
        gap: 14,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <Badge variant={variant}>{label}</Badge>
      <span style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-fg-muted)" }}>
        {slip.legs.length} LEGS
      </span>
      <span
        style={{
          fontFamily: fontStat,
          fontSize: 18,
          color: "var(--sa-fg-primary)",
        }}
      >
        {slip.combined_odds !== null ? `${slip.combined_odds.toFixed(2)}×` : "—"}
      </span>
      {slip.locked_at ? (
        <Pill>{`Locked ${new Date(slip.locked_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`}</Pill>
      ) : null}
      {slip.status === "settled" &&
      slip.outcome === "won" &&
      slip.potential_return !== null &&
      slip.potential_return > 0 ? (
        <BankToGoalCta
          source="daily_acca"
          sourceId={slip.slip_id}
          amount={slip.potential_return}
          sourceLabel={`Daily Acca ${slip.legs.length}-leg`}
        />
      ) : null}
    </div>
  );
}
